import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTierConfig, TIER_METADATA, getCostColor, getDefaultTierConfig } from '../../hooks/useTierConfig';
import { useConnections } from '../../hooks/useConnections';
import { useBilling } from '../../contexts/BillingContext';
import {
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  LockClosedIcon,
  PauseCircleIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../common/LoadingSpinner';

const TierScheduleConfig = ({ connectionId, onUpdate, className = '' }) => {
  const { tierConfig, loading, error, saving, updateTierConfig } = useTierConfig(connectionId);
  // Connection pause state — drives the banner at the top of the page so
  // users can tell at a glance whether tier collection will actually run
  // regardless of these settings. Without this banner the page looks
  // identical for paused vs active connections (BUGS.md: ST6).
  const { getConnectionById, loading: connectionsLoading } = useConnections();
  const { isFeatureEnabled, plan, getUpgradeUrl } = useBilling();
  const [localConfig, setLocalConfig] = useState(null);

  const connection = !connectionsLoading ? getConnectionById(connectionId) : null;
  const isPaused = connection && connection.enabled === false;

  // Check which tiers are enabled for this plan
  // Note: Tier 5 (Lineage) is controlled by lineage_enabled, not enabled_tiers
  const enabledTiers = plan?.features?.enabled_tiers || [1, 2];
  const isTierEnabled = (tierNumber) => {
    // Tier 5 (Lineage) is controlled by lineage_enabled feature
    if (tierNumber === 5) return isFeatureEnabled('lineage_enabled');
    return enabledTiers.includes(tierNumber) ||
           (tierNumber === 4 && isFeatureEnabled('tier4_enabled')) ||
           (tierNumber === 6 && isFeatureEnabled('tier6_enabled'));
  };

  // Map tier names to tier numbers
  const tierNameToNumber = {
    'tier1_discovery': 1,
    'tier2_schema': 2,
    'tier3_changes': 3,
    'tier4_profiling': 4,
    'tier5_lineage': 5,
    'tier6_usage': 6
  };

  const getRequiredPlan = (tierNumber) => {
    if (tierNumber <= 2) return null;
    if (tierNumber === 3) return 'Starter';
    if (tierNumber === 4) return 'Growth';
    if (tierNumber === 5) return 'Starter';  // Lineage is Starter+
    return 'Scale';  // Tier 6 is Scale only
  };

  // Sync tier config to local state
  useEffect(() => {
    if (tierConfig) {
      setLocalConfig(tierConfig);
    } else {
      setLocalConfig(getDefaultTierConfig());
    }
  }, [tierConfig]);

  const handleConfigChange = useCallback((tierName, field, value) => {
    setLocalConfig(prev => ({
      ...prev,
      [tierName]: {
        ...prev[tierName],
        [field]: value
      }
    }));
  }, []);

  const handleSave = async () => {
    if (!localConfig) return;

    const success = await updateTierConfig(localConfig);
    if (success && onUpdate) {
      onUpdate(localConfig);
    }
  };

  const handleReset = () => {
    setLocalConfig(tierConfig || getDefaultTierConfig());
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-gray-600">Loading tier configuration...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading configuration</h3>
            <p className="mt-1 text-sm text-red-700">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!localConfig) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No tier configuration available</p>
      </div>
    );
  }

  const hasChanges = JSON.stringify(localConfig) !== JSON.stringify(tierConfig);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <ClockIcon className="h-5 w-5 mr-2" />
            Metadata Collection Tiers
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Configure how frequently each tier of metadata collection runs
          </p>
        </div>

        {/* Save/Reset buttons */}
        {hasChanges && (
          <div className="flex items-center space-x-3">
            <button
              onClick={handleReset}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Reset
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <LoadingSpinner size="xs" className="mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Paused-connection banner. Tier collection is gated on
          database_connections.enabled (see backend/core/automation/
          simplified_scheduler.py:_check_and_queue_tier_tasks). Without
          this banner, users editing tier intervals on a paused
          connection have no signal that their settings won't take effect
          until they re-enable monitoring. BUGS.md: ST6 (2026-05-21). */}
      {isPaused && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
          <div className="flex">
            <PauseCircleIcon className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="ml-3">
              <h4 className="text-sm font-medium text-amber-900">
                This connection is paused
              </h4>
              <p className="mt-1 text-sm text-amber-800">
                Tier collection won't run on any schedule below until you
                re-enable monitoring on the connection details page. Your
                saved intervals are preserved and will apply automatically
                when monitoring resumes.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <InformationCircleIcon className="h-5 w-5 text-blue-400" />
          <div className="ml-3">
            <h4 className="text-sm font-medium text-blue-800">About Metadata Collection Tiers</h4>
            <p className="mt-1 text-sm text-blue-700">
              Sparvi uses a tiered approach to collect metadata. Lower tiers run more frequently
              and are lightweight, while higher tiers run less often and collect more detailed data.
            </p>
          </div>
        </div>
      </div>

      {/* Tier Configuration Cards */}
      <div className="space-y-4">
        {Object.entries(TIER_METADATA).map(([tierName, tierMeta]) => {
          const config = localConfig[tierName] || { enabled: false, interval_minutes: tierMeta.defaultInterval };
          const tierNumber = tierNameToNumber[tierName];
          const tierEnabled = isTierEnabled(tierNumber);
          const requiredPlan = getRequiredPlan(tierNumber);

          return (
            <div
              key={tierName}
              className={`border rounded-lg p-5 relative ${
                !tierEnabled
                  ? 'bg-amber-50 border-amber-200'
                  : config.enabled
                    ? 'bg-white border-gray-200'
                    : 'bg-gray-50 border-gray-100'
              }`}
            >
              {/* Locked Badge for unavailable tiers */}
              {!tierEnabled && (
                <div className="absolute -top-2 -right-2 bg-amber-500 text-white px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1 shadow-md">
                  <LockClosedIcon className="h-3 w-3" />
                  LOCKED
                </div>
              )}

              {/* Tier Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  {tierEnabled ? (
                    <input
                      type="checkbox"
                      checked={config.enabled}
                      onChange={(e) => handleConfigChange(tierName, 'enabled', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  ) : (
                    <LockClosedIcon className="h-4 w-4 text-amber-500" />
                  )}
                  <div className="ml-3">
                    <label className={`text-base font-medium ${tierEnabled ? 'text-gray-900' : 'text-amber-800'}`}>
                      {tierMeta.name}
                    </label>
                    <p className={`text-sm mt-0.5 ${tierEnabled ? 'text-gray-500' : 'text-amber-700'}`}>
                      {tierMeta.description}
                    </p>
                  </div>
                </div>

                {/* Cost Badge */}
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCostColor(tierMeta.cost)}`}>
                  {tierMeta.cost} cost
                </span>
              </div>

              {/* Upgrade prompt for locked tiers */}
              {!tierEnabled && (
                <div className="ml-7 mt-2 p-3 bg-amber-100 rounded-md border border-amber-200">
                  <p className="text-sm text-amber-800 mb-2">
                    This tier requires the <strong>{requiredPlan}</strong> plan or higher.
                  </p>
                  <Link
                    to={getUpgradeUrl ? getUpgradeUrl() : '/settings/billing'}
                    className="inline-flex items-center px-3 py-1.5 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700"
                  >
                    <LockClosedIcon className="h-4 w-4 mr-1.5" />
                    Upgrade to Unlock
                  </Link>
                </div>
              )}

              {/* Interval Configuration - only shown when enabled AND tier is available */}
              {tierEnabled && config.enabled && (
                <div className="ml-7 mt-4 pt-4 border-t border-gray-100">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Collection Frequency
                  </label>
                  <select
                    value={config.interval_minutes}
                    onChange={(e) => handleConfigChange(tierName, 'interval_minutes', parseInt(e.target.value, 10))}
                    className="block w-full max-w-xs border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    {tierMeta.intervalOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  {/* Optional tier warning */}
                  {tierMeta.optional && (
                    <p className="mt-2 text-xs text-amber-600">
                      This tier is resource-intensive and optional. Only enable if you need lineage tracking.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Dependency Note */}
      <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-sm text-gray-600">
        <strong className="text-gray-700">Note:</strong> Some tiers depend on data from other tiers.
        If you run Tier 4 (Profiling), the system will automatically ensure Tier 1 and Tier 2 data is up-to-date first.
      </div>
    </div>
  );
};

export default TierScheduleConfig;
